import { useCallback, useEffect, useRef, useState } from "react";

import LeadForm from "./LeadForm";
import { faqNodes, rootMenu } from "./faqData";
import { trackLead } from "utils/leadTracking";
import { gtag } from "utils/gtag";

import "./chatbot.scss";

// Labels for the two call-to-action chips and the reset chip. Everything else
// is a FAQ node whose label is its own question.
const SPECIAL_LABELS = {
  inquiry: "Talk to our team",
  partnership: "Partner with us",
  menu: "Back to menu"
};

const labelFor = (id) =>
  SPECIAL_LABELS[id] || (faqNodes[id] && faqNodes[id].q) || id;

// Phase 2 seam: to add AI answers later, make this async and call a serverless
// endpoint that holds the API key, falling back to the curated faqNodes here.
const resolveAnswer = (id) => faqNodes[id];

// Best-effort chatbot engagement events on the shared Google tag (see
// utils/gtag). trackLead handles the lead conversion itself.
const track = (action, label) => {
  gtag("event", action, { category: "chatbot", label });
};

const rootOptions = [...rootMenu, "partnership"];

const optionsForNode = (node) => {
  const ids = [...(node.next || [])];
  if (!ids.includes("inquiry")) ids.push("inquiry");
  if (!ids.includes("partnership")) ids.push("partnership");
  ids.push("menu");
  return ids;
};

const GREETING =
  "Hi! I'm the PSS assistant. I can answer common questions right away, connect you with our team, or explore a partnership. What would you like to know?";

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [options, setOptions] = useState(rootOptions);
  const [isTyping, setIsTyping] = useState(false);
  const [view, setView] = useState("menu"); // "menu" | "form"
  const [leadType, setLeadType] = useState("inquiry");

  const idRef = useRef(0);
  const threadRef = useRef(null);
  const typingTimer = useRef(null);

  const nextId = () => {
    idRef.current += 1;
    return idRef.current;
  };

  const pushMessage = useCallback((from, text) => {
    setMessages((prev) => [...prev, { id: nextId(), from, text }]);
  }, []);

  // Auto-scroll to the newest message / the form.
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages, isTyping, view]);

  // Clean up a pending "typing" timer on unmount.
  useEffect(() => () => clearTimeout(typingTimer.current), []);

  const openPanel = () => {
    setIsOpen(true);
    if (messages.length === 0) {
      pushMessage("bot", GREETING);
      track("open");
    }
  };

  const closePanel = () => setIsOpen(false);

  // Esc closes the panel.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen]);

  const answerNode = (id) => {
    const node = resolveAnswer(id);
    if (!node) return;
    pushMessage("user", node.q);
    setOptions([]);
    setIsTyping(true);
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      setIsTyping(false);
      pushMessage("bot", node.a);
      setOptions(optionsForNode(node));
    }, 550);
    track("faq_click", id);
  };

  const openForm = (type) => {
    setLeadType(type);
    pushMessage(
      "user",
      type === "partnership"
        ? "I'm interested in partnering with you"
        : "I'd like to talk to your team"
    );
    setOptions([]);
    setView("form");
    track(type === "partnership" ? "partnership_open" : "inquiry_open");
  };

  const backToMenu = (line) => {
    setView("menu");
    if (line) pushMessage("bot", line);
    setOptions(rootOptions);
  };

  const handleChip = (id) => {
    if (id === "menu") {
      backToMenu("What else can I help with?");
    } else if (id === "inquiry" || id === "partnership") {
      openForm(id);
    } else {
      answerNode(id);
    }
  };

  const handleSent = (typeLabel) => {
    backToMenu(
      `Thanks — your ${typeLabel.toLowerCase()} has reached our team. We'll review and reply by email. Anything else?`
    );
    // Same conversion as the contact page (a /thank-you virtual pageview +
    // generate_lead event), but without navigating away from the chat panel.
    trackLead({
      source: "chat",
      leadType: typeLabel.startsWith("Partnership") ? "partnership" : "inquiry"
    });
  };

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          className="chatbot__launcher"
          aria-label="Open the PSS assistant"
          onClick={openPanel}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M12 3C6.5 3 2 6.8 2 11.5c0 2.2 1 4.2 2.6 5.7-.1 1.2-.6 2.5-1.4 3.5-.2.3 0 .7.4.6 1.9-.4 3.4-1.1 4.5-1.8 1 .3 2.2.5 3.4.5 5.5 0 10-3.8 10-8.5S17.5 3 12 3Z"
            />
          </svg>
          <span className="chatbot__launcher-pulse" aria-hidden="true" />
        </button>
      )}

      {isOpen && (
        <div
          className="chatbot__panel"
          role="dialog"
          aria-label="PSS assistant"
        >
          <div className="chatbot__header">
            {view === "form" ? (
              <button
                type="button"
                className="chatbot__icon-btn"
                aria-label="Back to menu"
                onClick={() => backToMenu()}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 6l-6 6 6 6"
                  />
                </svg>
              </button>
            ) : (
              <span className="chatbot__avatar" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12 3C6.5 3 2 6.8 2 11.5c0 2.2 1 4.2 2.6 5.7-.1 1.2-.6 2.5-1.4 3.5-.2.3 0 .7.4.6 1.9-.4 3.4-1.1 4.5-1.8 1 .3 2.2.5 3.4.5 5.5 0 10-3.8 10-8.5S17.5 3 12 3Z"
                  />
                </svg>
              </span>
            )}
            <div className="chatbot__header-text">
              <p className="chatbot__title">PSS Assistant</p>
              <p className="chatbot__status">
                <span className="chatbot__dot" aria-hidden="true" />
                Typically answers instantly
              </p>
            </div>
            <button
              type="button"
              className="chatbot__icon-btn"
              aria-label="Close the assistant"
              onClick={closePanel}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  d="M6 6l12 12M18 6L6 18"
                />
              </svg>
            </button>
          </div>

          <div className="chatbot__thread" ref={threadRef}>
            {messages.map((m) => (
              <div
                key={m.id}
                className={`chatbot__bubble chatbot__bubble--${m.from}`}
              >
                {m.text}
              </div>
            ))}
            {isTyping && (
              <div className="chatbot__bubble chatbot__bubble--bot chatbot__typing">
                <span />
                <span />
                <span />
              </div>
            )}
            {view === "form" && (
              <LeadForm leadType={leadType} onSent={handleSent} />
            )}
          </div>

          {view === "menu" && options.length > 0 && (
            <div className="chatbot__chips">
              {options.map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`chatbot__chip${
                    id === "inquiry" || id === "partnership"
                      ? " chatbot__chip--cta"
                      : ""
                  }`}
                  onClick={() => handleChip(id)}
                >
                  {labelFor(id)}
                </button>
              ))}
            </div>
          )}

          <p className="chatbot__disclaimer">
            Answers are guidance only. Messages you send reach our team by
            email.
          </p>
        </div>
      )}
    </>
  );
};

export default ChatBot;
